import { AnimatePresence, motion } from 'motion/react'
import { ElementType, useEffect, useMemo, useRef, useState } from 'react'

import { astraTwMerge } from '../Utilities/astraTwMerge'

/**
 * AnimatedText — React 19 + motion + Tailwind + astraTwMerge
 *
 * Renders text using one of three prebaked animations: "slide", "fade", or "type".
 */

export type AnimatedTextVariant = 'slide' | 'fade' | 'type'

export type AnimatedTextProps = {
    text: string
    variant?: AnimatedTextVariant
    duration?: number
    speed?: number
    direction?: 'up' | 'down' | 'left' | 'right'
    distance?: number
    delay?: number
    once?: boolean
    as?: ElementType
    className?: string
    textClassName?: string
    caret?: boolean
}

const TypeText = ({
    text,
    className,
    textClassName,
    speed = 20,
    delay = 0,
    caret = true,
}: {
    text: string
    className?: string
    textClassName?: string
    speed?: number
    delay?: number
    caret?: boolean
}) => {
    const [shown, setShown] = useState(0)
    const rafRef = useRef<number | null>(null)

    useEffect(() => {
        setShown(0)
        const start = performance.now()
        const step = () => {
            const elapsed = (performance.now() - start) / 1000
            const chars = Math.floor(Math.max(0, elapsed - delay) * speed)
            setShown(prev => (chars !== prev ? Math.min(chars, text.length) : prev))
            if (chars < text.length) {
                rafRef.current = window.requestAnimationFrame(step)
            }
        }
        rafRef.current = window.requestAnimationFrame(step)
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current)
        }
    }, [text, speed, delay])

    const sliced = text.slice(0, shown)
    // const done = shown >= text.length

    return (
        <span className={astraTwMerge('inline-block align-baseline text-inherit', className)}>
            <span className={astraTwMerge('inline-block select-none whitespace-pre-wrap tracking-tight text-current', textClassName)}>{sliced}</span>
            {caret && (
                <motion.span
                    aria-hidden
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 0] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className={'ml-1 inline-block h-[1.1em] w-[0.6ch] rounded-sm bg-current align-[-0.2em]'}
                />
            )}
        </span>
    )
}

const dirToVector = (dir: NonNullable<AnimatedTextProps['direction']>) => {
    switch (dir) {
        case 'up':
            return { x: 0, y: 1 }
        case 'down':
            return { x: 0, y: -1 }
        case 'left':
            return { x: 1, y: 0 }
        case 'right':
            return { x: -1, y: 0 }
    }
}

export const AnimatedText = ({
    text,
    variant = 'fade',
    duration = 0.6,
    speed,
    direction = 'up',
    distance = 24,
    delay = 0,
    once = true,
    as: Tag = 'span',
    className,
    textClassName,
    caret = true,
}: AnimatedTextProps) => {
    const key = useMemo(() => `${variant}-${text}`, [variant, text])

    if (variant === 'type') {
        return <TypeText key={key} text={text} className={className} textClassName={textClassName} speed={speed} delay={delay} caret={caret} />
    }

    const vec = dirToVector(direction)
    const initialX = -vec.x * distance
    const initialY = -vec.y * distance

    let effectiveDuration = duration
    if (speed && speed > 0) {
        if (variant === 'slide') {
            const pixels = Math.max(1, distance)
            effectiveDuration = pixels / speed
        } else {
            effectiveDuration = Math.max(0.05, 1 / speed)
        }
    }

    return (
        <AnimatePresence mode={once ? 'wait' : 'sync'}>
            <Tag className={astraTwMerge('inline-block align-baseline text-inherit', className)}>
                <motion.span
                    key={key}
                    className={astraTwMerge('inline-block whitespace-pre-wrap text-current', textClassName)}
                    initial={variant === 'slide' ? { opacity: 0, x: initialX, y: initialY } : { opacity: 0 }}
                    animate={variant === 'slide' ? { opacity: 1, x: 0, y: 0 } : { opacity: 1 }}
                    transition={{ duration: effectiveDuration, delay }}
                >
                    {text}
                </motion.span>
            </Tag>
        </AnimatePresence>
    )
}
